import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { AncillaryServicesService } from "app_locobook/domain/ancillaryServices/useCases/ancillary-services.service";
import { FLUTTER_APPS } from "app/flutter.apps.generated";
import { ResponseForConvertOfferAfx2jsonType } from "@afidium/ts-facade";
import { ActivatedRoute, Params } from "@angular/router";
import { flatMap } from "rxjs/internal/operators";
import { Observable } from "rxjs";
import { ILoader } from "libraries/component/loader/ILoader";
import { INotification, NotificationType } from "libraries/interfaces/INotification";
import { Yii } from "libraries/interfaces/Yii";
import { LoaderType } from "libraries/component/loader/loader.component";
import {
    CacheQuotedAvailabilityService
} from "app_locobook/service/cache-quoted-availability/cache-quoted-availability.service";
import { IActionOutput } from "libraries/component/iframe-app-embedder/iframe-app-embedder.component";
import {
    IAirCreateBookingRequest,
    IAirCreateBookingRequestSegment, IAirCreateBookingRequestTraveller
} from "libraries/service/resources/model/booking/air/IAirCreateBooking";
import {
    AncillaryServicesPageService,
    IFareFamilyCollection
} from "app_locobook/pages/ancillary-services-page/ancillary-services-page.service";
import { EAction } from "libraries/interfaces/ancillary-services/e-actions";
import { IAirGetQuotedAvailabilityRequest } from "libraries/service/resources/model/booking/air/IAirQuotedAvailability";
import {
    AirBookingRequesterHelperService
} from "libraries/service/resources/air-booking/air-booking-requester-helper.service";
import { IQuote } from "libraries/service/resources/airTrip/IGetAirTripQuotes";
import { AirQuoteMappingService } from "libraries/service/resources/airQuote/air-quote-mapping.service";

export interface IValidateOutput {
    validatedOffer?: any;
    updatedBooking?: any;
    nextRequest?: IAirCreateBookingRequest;
    retrievedSeatMap?: any;
}

export interface IAncillaryServicesPageAdditionalData {
    servicesResponse?: any;
    servicesRequestBase?: any;
    seatMapResponse?: any;
}

@Component({
    selector: 'ancillary-services-page',
    template: `
        <loader *ngIf="loader.active" [message]="loader.message"></loader>
        <iframe-app-embedder-ancillary-services *ngIf="isIframeInDom"
                                                [isHidden]="loader.active"
                                                [isDynamicHeight]="false"
                                                [iframeName]="'ancillaryServicesPage'"
                                                [iframeSrc]="appUrl"
                                                [additionalData]="additionalData"
                                                [isFullHeight]="true"
                                                (onIframeEvent)="doAction($event)">
        </iframe-app-embedder-ancillary-services>
    `,
    styles: [`
        iframe-app-embedder-ancillary-services {
            height: calc(100vh - 80px);
        }

        loader {
            background: white;
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AncillaryServicesPageComponent implements OnInit, OnChanges {

    @Input()
    public servicesSelection: any;
    @Input()
    public seatMapResponse: any;
    @Input()
    public showPage: boolean;
    @Input()
    public actualFareFamily: IFareFamilyCollection;
    @Input()
    public segmentsForServices: IAirCreateBookingRequestSegment;
    @Input()
    public travellers: { [key: string]: IAirCreateBookingRequestTraveller };

    public appUrl: string;
    public isIframeInDom: boolean = true;

    public additionalData: IAncillaryServicesPageAdditionalData;

    public loader: ILoader = {
        active: true,
        type: LoaderType.large,
        message: Yii.t('wui-shared-js', 'Chargement des services en cours'),
    };

    public notification: INotification = {
        active: false,
        type: NotificationType.Error,
        message: Yii.t('wui-shared-js', 'Une erreur est survenue.'),
    };

    /*
    ##firstTransactionId

    Set from the response of the first get Quote With Service request.

    Must be reused each time a service Selection is validated
     */
    private firstTransactionId: string;

    private firstServicesRequestBase: IAirGetQuotedAvailabilityRequest;

    @Output()
    private readonly onExit: EventEmitter<void> = new EventEmitter<void>();
    @Output()
    private readonly onNoServices: EventEmitter<void> = new EventEmitter<void>();
    @Output()
    private readonly onError: EventEmitter<string> = new EventEmitter<string>();
    @Output()
    private readonly onValidate: EventEmitter<IValidateOutput> = new EventEmitter<IValidateOutput>();

    private bookingKey: string;

    constructor(
        private readonly ancillaryServicesService: AncillaryServicesService,
        private readonly route: ActivatedRoute,
        private readonly cacheQuotedAvailabilityService: CacheQuotedAvailabilityService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly ancillaryServicesPageService: AncillaryServicesPageService,
        private readonly airBookingRequesterHelperService: AirBookingRequesterHelperService,
        private readonly airQuoteMappingService: AirQuoteMappingService,
    ) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes?.showPage?.currentValue === false && changes?.showPage?.previousValue) {
            window.scroll(0, 0);
            this.loader.active = true;
            this.isIframeInDom = false;
            this.additionalData = {};
            setTimeout((): void => {
                this.isIframeInDom = true;
            }, 5);
            this.changeDetectorRef.detectChanges();
        }

        if (changes?.showPage?.currentValue) {
            window.scroll(0, 0);
            const parts: string[] = window.location.pathname.split('search')[1].split('/').filter((part: string): boolean => !part.includes('air'));

            let bookingKey: string = parts.find((part: string): boolean => part.includes('Q_'));
            if (!bookingKey) {
                bookingKey = parts.find((part: string): boolean => part.includes('Q.'));
            }

            if (bookingKey === this.bookingKey && this.servicesSelection) {
                // back on page

                this.additionalData = {
                    servicesResponse: {
                        "error": false,
                        "warning": false,
                        "code": 200,
                        "message": null,
                        data: this.servicesSelection,
                    },
                    servicesRequestBase: {
                        ...this.firstServicesRequestBase,
                        segment: this.segmentsForServices,
                        transactionId: this.firstTransactionId,
                    },
                    seatMapResponse: this.seatMapResponse,
                };
                this.changeDetectorRef.detectChanges();
                this.loader.active = false;

            } else {
                // first opening

                this.loader.active = true;
                this.bookingKey = bookingKey;
                this.ancillaryServicesService.getQuoteWithService(bookingKey, this.actualFareFamily, this.segmentsForServices, this.travellers)
                    .subscribe({
                        next: (response: ResponseForConvertOfferAfx2jsonType): void => {
                            this.firstTransactionId = response.offer.transactionId;
                            this.firstServicesRequestBase = {
                                ...this.cacheQuotedAvailabilityService.getRequest(bookingKey),
                                // @ts-ignore
                                //ancillaryServices App needs up to date FareFamily Syscode (to match new transactionID)
                                fareFamily: this.ancillaryServicesPageService.getFareFamiliesWithUpdatedSyscodeValue(this.actualFareFamily, response),
                                segment: this.segmentsForServices,
                                transactionId: this.firstTransactionId,
                            };

                            this.loader.active = false;

                            if (!response.trip.serviceArray?.length) {
                                this.onNoServices.emit();
                            } else {

                                this.loader.active = false;
                                this.additionalData = {
                                    servicesResponse: {
                                        error: false,
                                        warning: false,
                                        code: 200,
                                        message: null,
                                        data: response,
                                    },
                                    servicesRequestBase: this.firstServicesRequestBase,
                                };
                                this.changeDetectorRef.detectChanges();

                            }
                        },
                        error: (e: Error): void => {
                            this.onError.emit(e.message);
                            this.loader.active = false;
                            console.error(e);
                        }
                    });
            }

        }
    }

    public ngOnInit(): void {
        this.appUrl = FLUTTER_APPS.ancillary_services_app;
    }

    public doAction(action: IActionOutput): void {
        switch (action.name) {
            case EAction.serviceValidation:
                const booking: Partial<IQuote> = this.airQuoteMappingService.mapGqaNewToOld(action.validatedOffer.data);
                const valueToEmit: IValidateOutput = {
                    validatedOffer: action.validatedOffer.data,
                    nextRequest: {
                        ...action.nextRequest,
                        fareFamily: booking.fareFamily,
                        transactionId: (action.validatedOffer.data as ResponseForConvertOfferAfx2jsonType).offer.transactionId,
                        segment: this.airBookingRequesterHelperService.simplifySegments(booking.segment),
                    },
                };
                if (action.retrievedSeatMap) {
                    valueToEmit.retrievedSeatMap = action.retrievedSeatMap.data ? action.retrievedSeatMap.data : action.retrievedSeatMap;
                }
                this.onValidate.emit(valueToEmit);
                break;
            case EAction.serviceCancellation:
                this.bookingKey = null;
                this.onExit.emit();
                break;
            default:
                break;
        }
    }

}
